import React from "react";
import "./Card.css";
import styled from "@emotion/styled";
import { Typography } from "../Typography";
import { Tooltip } from "../Tooltip";
import { BRAND_HEROBLUE } from "../../design-tokens";
import { SvgIcon, SvgNames } from "../../assets/icons/SvgIcon";

const CardBaseContent = ({
  leftContent,
  rightContent,
  toolTipContent,
}: CardBaseContentProps) => {
  return (
    <div className="card-base-content">
      {leftContent && (
        <div className="card-base-left-content">{leftContent}</div>
      )}
      {toolTipContent && (
        <div className="card-base-tooltip">{toolTipContent}</div>
      )}
      {rightContent && (
        <div className="card-base-right-content">{rightContent}</div>
      )}
    </div>
  );
};

const CardHeader = ({
  title,
  subtitle,
  leftContent,
  rightContent,
  children,
  className,
  onClick,
  toolTipText,
  contactType,
}: CardHeaderProps) => {
  let headerLeftContent: React.ReactNode = leftContent;
  if (title) {
    headerLeftContent = (
      <div className="card-header-title">
        <Typography>{title}</Typography>
        {subtitle && <Typography role="secondary">{subtitle}</Typography>}
      </div>
    );
  }

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
    <div
      data-testid="card-header"
      className={`card-header ${className ?? ""}`}
      onClick={onClick}
    >
      {children || (
        <CardBaseContent
          leftContent={headerLeftContent}
          rightContent={rightContent}
          toolTipContent={
            toolTipText && (
              <Tooltip
                content={toolTipText}
                direction="top-left"
                customTestId={`experience-ui-components--Card--${
                  contactType ? contactType + "-card--" : ""
                }tooltip`}
                className={"icon-tooltip"}
              >
                <SvgIcon
                  style={{ marginLeft: "50px" }}
                  data-testid={`experience-ui-components--Card--${
                    contactType ? contactType + "-card--" : ""
                  }tooltip-button`}
                  color={BRAND_HEROBLUE}
                  width={20}
                  height={20}
                  name={SvgNames.InfoOutline}
                />
              </Tooltip>
            )
          }
        />
      )}
    </div>
  );
};

const CardFooter = ({
  leftContent,
  rightContent,
  children,
  className,
  onClick,
}: CardFooterProps) => {
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
    <div
      data-testid="card-footer"
      className={`card-footer ${className ?? ""}`}
      onClick={onClick}
    >
      {children || (
        <CardBaseContent
          leftContent={leftContent}
          rightContent={rightContent}
        />
      )}
    </div>
  );
};

const CardSectionDivider = ({ className }: CardSectionDividerProps) => {
  return (
    <hr
      data-testid="card-section-divider"
      className={`card-section-divider ${className ?? ""}`}
    />
  );
};

const CardStyledInformationContent = ({
  className,
  infoGroupHeading,
  fields,
  dataTestId = "card-styled-information-content",
}: CardStyledInformationContentProps) => {
  return (
    <div
      data-testid={dataTestId}
      className={`card-styled-information-content ${className ?? ""}`}
    >
      {infoGroupHeading && (
        <p className="card-styled-information-content--infoGroupHeading">
          {infoGroupHeading}
        </p>
      )}
      <div className="card-styled-information-content--infoGroup">
        {fields && (
          <CardInfoContainer>
            {fields.map((field, index) => (
              <CardSection key={index}>
                <div className="card-styled-information-content--title">
                  {field.title}
                </div>
                <div className="card-styled-information-content--value">
                  {field.value}
                </div>
              </CardSection>
            ))}
          </CardInfoContainer>
        )}
      </div>
    </div>
  );
};

const Card = ({
  children,
  bgColor = "default",
  className,
  onClick,
  dataTestId = "card",
}: CardProps) => {
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
    <div
      data-testid={dataTestId}
      className={`card ${bgColor === "white" ? "white-bg" : ""} ${
        className ?? ""
      }`}
      onClick={onClick}
    >
      {children}
    </div>
  );
};

interface CardBaseProps {
  children?: React.ReactNode;
  className?: string;
  onClick?: () => void;
  dataTestId?: string;
}

interface CardHeaderProps extends CardBaseProps {
  title?: string;
  subtitle?: string;
  leftContent?: React.ReactNode;
  rightContent?: React.ReactNode;
  toolTipText?: string;
  contactType?: string;
}

interface CardFooterProps extends CardBaseProps {
  leftContent?: React.ReactNode;
  rightContent?: React.ReactNode;
}

interface CardSectionDividerProps {
  className?: string;
}

interface CardProps extends CardBaseProps {
  bgColor?: "white" | "default";
}

type CardBaseContentProps = {
  leftContent?: React.ReactNode;
  rightContent?: React.ReactNode;
  toolTipContent?: React.ReactNode;
};

type CardStyledInformationContentProps = {
  className?: string;
  dataTestId?: string;
  infoGroupHeading?: string;
  fields: Array<{
    title: string;
    value: string;
  }>;
};

const CardInfoContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
`;

const CardSection = styled.div`
  padding: 8px 0px 8px 0px;
`;

export type {
  CardProps,
  CardHeaderProps,
  CardFooterProps,
  CardSectionDividerProps,
};
export {
  Card,
  CardHeader,
  CardFooter,
  CardSectionDivider,
  CardStyledInformationContent,
};
